// ✅ src/pages/Asset/AssetScrapEntry.jsx  (FULL FILE - API INTEGRATED + LOCALSTORAGE HEADERS)
// Backend mount:
//   app.use("/api/scrapMaster", scrapMaster);
//
// Expected endpoints:
//   GET  http://nissi-india.com/api/scrapMaster/branches
//   GET  http://nissi-india.com/api/scrapMaster/assetTypes?branchName=Branch 8
//   GET  http://nissi-india.com/api/scrapMaster/subtypes?assetTypeId=
//   GET  http://nissi-india.com/api/scrapMaster/idcodes?branchName=&assetTypeId=&assetSubTypeId=
//   POST http://nissi-india.com/api/scrapMaster/save

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/scrapMaster";

const AssetScrapEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ safe localStorage getter
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ dropdown sources
  const [branches, setBranches] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  // ✅ ID Code grid rows
  const [gridRows, setGridRows] = useState([]);

  const [loadingDrop, setLoadingDrop] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ form
  const [formData, setFormData] = useState({
    assetType: "",
    assetSubType: "",
    location: "",
    scrapDate: "",
    reasonForScrap: "",
    preparedBy: "",
    proposedBy: "",
    approvedBy: "",
  });

  const [errors, setErrors] = useState({});

  // ---------- helpers ----------
  const normalizeBranch = (b) => {
    const raw = b?.raw || b || {};
    const value =
      b?.value ??
      raw?.fld_Branchid ??
      raw?.fld_branchid ??
      raw?.BranchId ??
      raw?.branchid ??
      "";
    const label =
      (b?.label && String(b.label).trim()) ||
      raw?.fld_BranchName ||
      raw?.fld_branchName ||
      raw?.fld_branchname ||
      raw?.BranchName ||
      raw?.branchName ||
      (value ? `Branch ${value}` : "");

    return { value: String(value), label: String(label), raw };
  };

  const normalizeOpt = (o, valueKeys, labelKeys) => {
    const raw = o?.raw || o || {};

    const pick = (obj, keys) => {
      for (const k of keys) {
        if (
          obj?.[k] !== undefined &&
          obj?.[k] !== null &&
          String(obj[k]).trim() !== ""
        ) {
          return obj[k];
        }
      }
      return "";
    };

    const value = o?.value || pick(raw, valueKeys);
    const label =
      (o?.label && String(o.label).trim()) ||
      pick(raw, labelKeys) ||
      String(value);

    return { value: String(value), label: String(label), raw };
  };

  const getSelectedBranchName = useMemo(() => {
    const b = branches.find((x) => x.value === String(formData.location));
    return b?.label || "";
  }, [branches, formData.location]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.assetType) newErrors.assetType = "Asset Type is required";
    if (!formData.assetSubType) newErrors.assetSubType = "Asset Sub Type is required";
    if (!formData.scrapDate) newErrors.scrapDate = "Scrap Date is required";
    if (!formData.reasonForScrap.trim()) {
      newErrors.reasonForScrap = "Reason for Scrap is required";
    }
    if (!formData.preparedBy.trim()) newErrors.preparedBy = "Prepared By is required";
    if (!formData.proposedBy.trim()) newErrors.proposedBy = "Proposed By is required";
    if (!formData.approvedBy.trim()) newErrors.approvedBy = "Approved By is required";

    const selectedValid = gridRows.some(
      (r) => r.selected && Number(r.scrapQty) > 0
    );
    if (!selectedValid) {
      newErrors.grid = "Select at least one ID Code and enter Scrap Qty > 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Load initial branches ----------
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoadingDrop(true);

        const res = await axios.get(`${API_BASE}/branches`, {
          headers: commonHeaders,
        });

        const list = res?.data?.data || [];
        const norm = list.map(normalizeBranch);
        setBranches(norm);

        const defaultBranchId =
          getLS("branchId") ||
          getLS("branchid") ||
          getLS("BranchId") ||
          (norm[0]?.value ?? "");

        if (defaultBranchId) {
          setFormData((p) => ({ ...p, location: String(defaultBranchId) }));
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load branches");
      } finally {
        setLoadingDrop(false);
      }
    };

    loadBranches();
  }, [commonHeaders]);

  // ---------- Load asset types whenever branch changes ----------
  useEffect(() => {
    const loadAssetTypes = async () => {
      try {
        if (!formData.location || !getSelectedBranchName) return;

        setLoadingDrop(true);

        setAssetTypes([]);
        setSubtypes([]);
        setGridRows([]);
        setFormData((p) => ({ ...p, assetType: "", assetSubType: "" }));

        const res = await axios.get(`${API_BASE}/assetTypes`, {
          params: { branchName: getSelectedBranchName },
          headers: commonHeaders,
        });

        const list = res?.data?.data || [];
        const norm = list.map((x) =>
          normalizeOpt(
            x,
            ["fld_assettypeid", "fld_AssetTypeId", "AssetTypeId", "id", "value"],
            ["AssetType", "fld_AssetTypeName", "AssetTypeName", "name", "label"]
          )
        );

        setAssetTypes(norm);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load asset types");
      } finally {
        setLoadingDrop(false);
      }
    };

    loadAssetTypes();
  }, [formData.location, getSelectedBranchName, commonHeaders]);

  // ---------- Load subtypes whenever asset type changes ----------
  useEffect(() => {
    const loadSubtypes = async () => {
      try {
        if (!formData.assetType) return;

        setLoadingDrop(true);
        setSubtypes([]);
        setGridRows([]);
        setFormData((p) => ({ ...p, assetSubType: "" }));

        const res = await axios.get(`${API_BASE}/subtypes`, {
          params: { assetTypeId: formData.assetType },
          headers: commonHeaders,
        });

        const list = res?.data?.data || [];
        const norm = list.map((x) =>
          normalizeOpt(
            x,
            ["fld_AssetSubTypeID", "fld_assetsubtypeid", "AssetSubTypeId", "id", "value"],
            ["fld_AssetSubTypeName", "fld_assetsubtypename", "AssetSubTypeName", "name", "label"]
          )
        );

        setSubtypes(norm);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load asset sub types");
      } finally {
        setLoadingDrop(false);
      }
    };

    loadSubtypes();
  }, [formData.assetType, commonHeaders]);

  // ---------- Load ID Codes grid whenever subtype changes ----------
  useEffect(() => {
    const loadGrid = async () => {
      try {
        if (!formData.assetSubType) return;
        if (!formData.assetType) return;
        if (!getSelectedBranchName) return;

        setLoadingGrid(true);
        setGridRows([]);

        const res = await axios.get(`${API_BASE}/idcodes`, {
          params: {
            branchName: getSelectedBranchName,
            assetTypeId: formData.assetType,
            assetSubTypeId: formData.assetSubType,
          },
          headers: commonHeaders,
        });

        const rows = res?.data?.data || [];

        const mapped = rows.map((r) => {
          const idCode =
            r.fld_IdCodeno ||
            r.fld_IdCode ||
            r.IdCode ||
            r.idCode ||
            r.fld_assetidcode ||
            "";

          const assetId =
            r.fld_Assetid ||
            r.AssetId ||
            r.assetId ||
            r.fld_assetmasterid ||
            "";

          const qty = Number(r.fld_noofqty ?? r.qty ?? r.Qty ?? 0) || 0;
          const purchaseValue =
            Number(r.fld_PurchaseValue ?? r.purchaseValue ?? r.PurchaseValue ?? 0) || 0;

          return {
            selected: false,
            assetId: String(assetId),
            idCode: String(idCode),
            qty,
            scrapQty: 0,
            balance: qty,
            purchaseValue,
            raw: r,
          };
        });

        setGridRows(mapped);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load ID Codes");
      } finally {
        setLoadingGrid(false);
      }
    };

    loadGrid();
  }, [formData.assetSubType, formData.assetType, getSelectedBranchName, commonHeaders]);

  // ---------- Grid handlers ----------
  const updateGridRow = (idx, patch) => {
    setGridRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...patch };

      const row = copy[idx];
      if ("scrapQty" in patch || "selected" in patch) {
        const q = Number(row.qty) || 0;
        const s = Number(row.scrapQty) || 0;

        if (row.selected) {
          if (s > 0 && s <= q) {
            row.balance = q - s;
          } else if (s === 0) {
            row.balance = q;
          } else {
            row.scrapQty = 0;
            row.balance = q;
          }
        } else {
          row.scrapQty = 0;
          row.balance = q;
        }
      }

      return copy;
    });
  };

  const totals = useMemo(() => {
    let qty = 0;
    let scrap = 0;
    let bal = 0;

    gridRows.forEach((r) => {
      qty += Number(r.qty) || 0;
      scrap += r.selected ? Number(r.scrapQty) || 0 : 0;
      bal += Number(r.balance) || 0;
    });

    return { qty, scrap, bal };
  }, [gridRows]);

  // ---------- Submit ----------
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);

      const items = gridRows
        .filter((r) => r.selected && Number(r.scrapQty) > 0)
        .map((r) => ({
          assetId: r.assetId,
          idCode: r.idCode,
          qty: r.qty,
          scrapQty: Number(r.scrapQty) || 0,
          balance: r.balance,
          purchaseValue: r.purchaseValue,
        }));

      const payload = {
        branchid: branchId,
        toBranchId: formData.location,
        assetTypeId: formData.assetType,
        assetSubTypeId: formData.assetSubType,
        scrapDate: formData.scrapDate,
        reason: formData.reasonForScrap,
        preparedBy: formData.preparedBy,
        proposedBy: formData.proposedBy,
        approvedBy: formData.approvedBy,
        userId,
        items,
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Saved Successfully");
        navigate("/Asset/AssetScrapListing");
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save scrap entry");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET SCRAP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
              onClick={handleBack}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
            >
              <FaArrowLeft />
              Back
            </button>
        <div
          className={`p-8 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Scrap Entry
            </h1>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Asset Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.assetType}
                  onChange={(e) => handleInputChange("assetType", e.target.value)}
                  // disabled={loadingDrop || !assetTypes.length}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.assetType ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="">Select</option>
                  {assetTypes.map((a, idx) => (
                    <option key={idx} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
                {errors.assetType && <p className="text-red-500 text-sm mt-1">{errors.assetType}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  disabled={loadingDrop}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.location ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="">Select</option>
                  {branches.map((b, idx) => (
                    <option key={idx} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
                {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Asset Sub Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.assetSubType}
                  onChange={(e) => handleInputChange("assetSubType", e.target.value)}
                  // disabled={loadingDrop || !formData.assetType}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.assetSubType ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="">Select</option>
                  {subtypes.map((s, idx) => (
                    <option key={idx} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {errors.assetSubType && <p className="text-red-500 text-sm mt-1">{errors.assetSubType}</p>}
              </div>
              <div />
            </div>

            <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className={`px-4 py-3 font-semibold ${isDark ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-900"}`}>
                ID Codes
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Select</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">ID Code</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Qty</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Scrap Qty</th>
                      <th className="px-3 py-3 text-center font-semibold">Balance</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingGrid ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : gridRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center">
                          No ID Codes
                        </td>
                      </tr>
                    ) : (
                      gridRows.map((r, idx) => (
                        <tr
                          key={idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                            <input
                              type="checkbox"
                              checked={!!r.selected}
                              onChange={(e) =>
                                updateGridRow(idx, { selected: e.target.checked })
                              }
                            />
                          </td>

                          <td className={`px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.idCode}
                          </td>

                          <td className={`px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.qty}
                          </td>

                          <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                            <input
                              type="number"
                              min={0}
                              disabled={!r.selected}
                              value={r.scrapQty}
                              onChange={(e) =>
                                updateGridRow(idx, { scrapQty: e.target.value })
                              }
                              className={`w-24 px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-3 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.balance}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  <tfoot>
                    <tr className={`${isDark ? "bg-gray-800" : "bg-gray-100"} font-semibold`}>
                      <td className="px-3 py-3 text-center" colSpan={2}>
                        Total
                      </td>
                      <td className="px-3 py-3 text-center">{totals.qty}</td>
                      <td className="px-3 py-3 text-center">{totals.scrap}</td>
                      <td className="px-3 py-3 text-center">{totals.bal}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {errors.grid && <p className="text-red-500 text-sm">{errors.grid}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Scrap Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.scrapDate}
                  onChange={(e) => handleInputChange("scrapDate", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.scrapDate ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                {errors.scrapDate && <p className="text-red-500 text-sm mt-1">{errors.scrapDate}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Reason for Scrap <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.reasonForScrap}
                  onChange={(e) => handleInputChange("reasonForScrap", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                    errors.reasonForScrap ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  placeholder="Enter reason for scrapping this asset..."
                />
                {errors.reasonForScrap && <p className="text-red-500 text-sm mt-1">{errors.reasonForScrap}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Prepared By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.preparedBy}
                  onChange={(e) => handleInputChange("preparedBy", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.preparedBy ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  placeholder="Enter name..."
                />
                {errors.preparedBy && <p className="text-red-500 text-sm mt-1">{errors.preparedBy}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Proposed By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.proposedBy}
                  onChange={(e) => handleInputChange("proposedBy", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.proposedBy ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  placeholder="Enter name..."
                />
                {errors.proposedBy && <p className="text-red-500 text-sm mt-1">{errors.proposedBy}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Approved By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.approvedBy}
                  onChange={(e) => handleInputChange("approvedBy", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.approvedBy ? "border-red-500" : ""
                  } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  placeholder="Enter name..."
                />
                {errors.approvedBy && <p className="text-red-500 text-sm mt-1">{errors.approvedBy}</p>}
              </div>
              <div />
            </div>

            <div className="flex justify-end pt-6">
              <button
                onClick={handleSubmit}
                disabled={saving}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  saving ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaCheck />
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetScrapEntry;